import dayjs from "dayjs";
import * as jose from "jose";
import { useCookies } from "vue3-cookies";

import { Statsig } from "@/env/statsig/client";
import { EventBus } from "@/store/eventBus";

const SECURE_COOKIE = "x-lacp-secure";
const PROFILE_COOKIE = "x-lacp-profile";
const SESSION_TIMEOUT_COOKIE = "x-lacp-session-timeout";
// Mirrors the deployed ui-deployment-container SESSION_TIMEOUT (the cookie's Max-Age).
const DEFAULT_SESSION_TIMEOUT_SECONDS = 28800;

// Every cookie that identifies an authenticated session. Cleared on forced logout
// so no "zombie" state remains to issue unauthorized calls.
const SESSION_COOKIES = [
  SECURE_COOKIE,
  PROFILE_COOKIE,
  SESSION_TIMEOUT_COOKIE,
  "session-id",
  "CDP_TIMEOUT_SEEN",
];

function cookieJar() {
  return useCookies().cookies;
}

/**
 * Both the profile and secure cookies must be present for the session to be
 * considered established. Non-browser environments (SSR / node) are treated as
 * authenticated to avoid touching `document`.
 */
export function hasSessionCookies(): boolean {
  if (typeof document === "undefined") return true;
  const cookies = cookieJar();
  return Boolean(cookies.get(PROFILE_COOKIE)) && Boolean(cookies.get(SECURE_COOKIE));
}

/**
 * Decode the secure JWT and return its expiration as a dayjs instance, or null
 * when the token is missing/malformed/without an `exp` claim.
 */
export function getTokenExpiry(): dayjs.Dayjs | null {
  if (typeof document === "undefined") return null;
  const token = cookieJar().get(SECURE_COOKIE);
  if (!token || token === "null") return null;
  try {
    const { exp } = jose.decodeJwt(token);
    if (!exp) return null;
    return dayjs.unix(exp);
  } catch {
    return null;
  }
}

export function sessionTimeoutSeconds(): number {
  const configured = Number(globalThis.env?.SESSION_TIMEOUT_SECONDS);
  return Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_SESSION_TIMEOUT_SECONDS;
}

/**
 * The `x-lacp-session-timeout` cookie holds the session START time (ISO 8601),
 * set by ui-deployment-container; the expiry is start + SESSION_TIMEOUT_SECONDS.
 * Returns null when the cookie is missing, unset (`"0"`), or not a valid date.
 */
export function getSessionTimeoutExpiry(): dayjs.Dayjs | null {
  if (typeof document === "undefined") return null;
  const value = cookieJar().get(SESSION_TIMEOUT_COOKIE);
  if (!value || value === "0" || value === "null") return null;
  const start = dayjs(value);
  return start.isValid() ? start.add(sessionTimeoutSeconds(), "second") : null;
}

/**
 * A missing or invalid token counts as expired. A valid token is expired once
 * the current time reaches its `exp`.
 */
export function isTokenExpired(): boolean {
  const expiry = getTokenExpiry();
  if (!expiry) return true;
  const now = dayjs();
  return now.isSame(expiry) || now.isAfter(expiry);
}

/**
 * Guards {@link forceLogout} against re-entry. Several in-flight requests can all
 * return 403 in the same tick; without this flag each one would clear cookies,
 * emit a "Session expired" banner, and reassign `location.href`, stacking N
 * identical banners. The first call wins; the rest are no-ops.
 */
let hasLoggedOut = false;

/**
 * Cached value of the environment-scoped session-timeout gate, shared by this
 * module and SessionTimeout.vue so the gate is resolved once per page.
 *
 * It is cached rather than awaited per call because {@link handleUnauthorizedResponse}
 * must stay synchronous: axios interceptors cannot await, and the gate-off
 * `localStorage.clear()` has to run before the request promise rejects, as it did
 * before this feature. Resolution is lazy (not at module load) to avoid
 * constructing a Statsig client as an import side effect.
 */
let gateEnabled = false;
let gatePromise: Promise<void> | null = null;

/**
 * Resolve the session-timeout gate once. Safe to call repeatedly.
 * SessionTimeout.vue calls this on mount, which primes the value well before any
 * 403 can arrive; a 403 that still beats it falls back to the base path.
 */
export function whenSessionGateReady(): Promise<void> {
  gatePromise ??= Statsig.environmentFeatureGate("ENABLE_SESSION_TIMEOUT_WARNING")
    .then((enabled) => {
      gateEnabled = enabled;
    })
    .catch(() => {
      gateEnabled = false;
    });
  return gatePromise;
}

export function isSessionGateEnabled(): boolean {
  return gateEnabled;
}

/**
 * Testing helper: drop the cached gate value so the next read re-resolves.
 */
export function resetSessionGateCache(): void {
  gateEnabled = false;
  gatePromise = null;
}
/**
 * Testing/HMR helper: reset the one-shot logout guard.
 */
export function resetForceLogoutGuard(): void {
  hasLoggedOut = false;
}

/**
 * Clear all session state and send the user through the UnAuth logout flow.
 * Surfaces a non-intrusive banner so the user understands why they were signed out.
 * Idempotent: only the first invocation performs the logout side effects.
 */
export function forceLogout(): void {
  if (hasLoggedOut) return;
  hasLoggedOut = true;

  try {
    globalThis.localStorage?.clear();
  } catch {
    // localStorage may be unavailable; ignore.
  }

  const cookies = cookieJar();
  SESSION_COOKIES.forEach((name) => {
    try {
      cookies.remove(name);
    } catch {
      // Ignore cookies that cannot be removed.
    }
  });

  EventBus.emit("showBanner", {
    type: "ERROR",
    title: "Session expired",
    message: "Your session has expired. Please sign in again.",
  });

  if (globalThis.location !== undefined) {
    globalThis.location.href = "/sso-logout";
  }
}

/**
 * Central handler for forbidden API responses, shared by every http client.
 * Only 403 is handled; 401 is deliberately not intercepted (see the http
 * interceptors, which narrow to 403).
 *
 * Gated by the environment-scoped `enable_session_timeout_warning` Statsig gate:
 * - Gate OFF (default / base behavior): a 403 clears localStorage. This is
 *   byte-for-byte the behavior that shipped before this feature.
 * - Gate ON: a 403 first checks the profile/secure cookies and the token
 *   expiry; if the cookies exist and the token is still valid the session is
 *   left intact, otherwise the user is logged out.
 */
export function handleUnauthorizedResponse(status?: number): void {
  if (status !== 403) return;

  if (!gateEnabled) {
    // Base behavior: a 403 clears localStorage.
    try {
      globalThis.localStorage?.clear();
    } catch {
      // localStorage may be unavailable; ignore.
    }
    return;
  }

  if (hasSessionCookies() && !isTokenExpired()) return;
  forceLogout();
}
